import { useState } from 'react';

import { Checkbox } from '@/shadcn/checkbox';
import { cn } from '@/shadcn/lib/utils';

import { isLockedWidget } from '@/components/forms/utils/links';
import { useDynamicProps } from '@/hooks/useDynamicProps';
import { usePropsSynchronization } from '@/hooks/usePropsSynchronization';
import { TestId } from '@/types/testId';
import type { WidgetWriterProps } from '../widget.type';
import { arraysMatch, deselectValue, selectValue } from './checkboxes.utils';

type CheckState = boolean | 'indeterminate';

type Option = {
  label?: string;
  value: string;
};

type Props = {
  options: {
    enumOptions: Option[];
    enumDisabled: string[];
    inline: boolean;
  };
  /**
   * New field to avoid nesting options under enumOptions on the configuration side when declaring dynamic Props,
   * this will simplify the syntax.
   */
  enum?: Option[];
} & WidgetWriterProps;

export const CheckboxesWidget = (rjsfProps: Props) => {
  const props = useDynamicProps(rjsfProps);
  usePropsSynchronization(props, rjsfProps, v => {
    props.onChange(v.filter(Boolean));
  });

  /**
   * Building an array of string values
   * in order to match with an array of results values.
   */

  const enumDisabled = props.options.enumDisabled || [];
  const isInline = props.options.inline;
  const options = props.enum || props.options.enumOptions || [];

  const enumOptionsValues: string[] = Object.values(options)
    .map(e => e.value)
    .filter(
      option => !enumDisabled.includes(option) || props.value.includes(option)
    );

  const [tempEnumOptions, setTempEnumOptions] = useState(enumOptionsValues);

  /**
   * Check if the selectMenu option value has changed.
   * If so, we clear the array of results values.
   */
  if (!arraysMatch(enumOptionsValues, tempEnumOptions)) {
    setTempEnumOptions(enumOptionsValues);
    props.onChange([]);
  }

  const handleSingleCheck = (checkState: CheckState, optionValue: string) => {
    const all = options.map(({ value }) => value);

    if (checkState === true) {
      return props.onChange(selectValue(optionValue, props.value, all));
    }
    return props.onChange(deselectValue(optionValue, props.value));
  };

  const handleAllChecked = (checkState: CheckState) => {
    if (checkState === false) {
      const disabledOptionsSelected = props.value.filter(option =>
        enumDisabled.includes(option)
      );
      return props.onChange(disabledOptionsSelected);
    }
    if (checkState === true) {
      return props.onChange(enumOptionsValues);
    }
  };

  // check if the widget is locked because of forms links
  const isLocked = isLockedWidget(props.id, props.formContext.links);

  const allDisabled = props.disabled || props.readonly || isLocked;
  const isAllCheckDisplay = options.length > 1;

  const getAllCheckState = (): CheckState => {
    if (arraysMatch(enumOptionsValues, props.value)) return true;
    if (props.value.length === 0) return false;
    return 'indeterminate';
  };

  const allCheckState = getAllCheckState();

  return (
    <div
      className={cn('flex flex-col gap-3', isInline && 'flex-row items-center')}
      data-cy={`checkboxes-${props.name}`}
      id={props.id}
    >
      {isAllCheckDisplay && (
        <div className="flex items-center">
          <Checkbox
            aria-invalid={props.invalid}
            checked={allCheckState}
            data-cy={`checkboxes-check-all-${props.name}`}
            data-testid={TestId.FormWriter.Checkboxes.CheckBoxesAll}
            disabled={allDisabled}
            id={`check-all-${props.id}`}
            onCheckedChange={handleAllChecked}
          />
          <label
            className="cursor-pointer select-none pl-2 font-medium text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            data-testid={TestId.FormWriter.Checkboxes.CheckboxesLabel}
            htmlFor={`check-all-${props.id}`}
          >
            Select all
          </label>
        </div>
      )}

      {options.map(({ value: optionValue, label }) => {
        const checked = props.value.indexOf(optionValue) !== -1;
        const itemDisabled =
          enumDisabled && enumDisabled.indexOf(optionValue) !== -1;
        return (
          <div
            className={cn('flex items-center', isAllCheckDisplay && 'ml-5')}
            key={`${props.id}-${optionValue}`}
          >
            <Checkbox
              aria-invalid={props.invalid}
              checked={checked}
              data-cy={`checkboxes-${props.name}-${optionValue}`}
              data-testid={TestId.FormWriter.Checkboxes.Checkboxes}
              disabled={allDisabled || itemDisabled}
              id={`${props.id}-${optionValue}`}
              onCheckedChange={e => handleSingleCheck(e, optionValue)}
            />
            <label
              className="cursor-pointer pl-2 font-medium text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              data-testid={TestId.FormWriter.Checkboxes.CheckboxesLabel}
              htmlFor={`${props.id}-${optionValue}`}
            >
              {label}
            </label>
          </div>
        );
      })}
    </div>
  );
};
